import React, { useMemo } from "react";

// ── Types ────────────────────────────────────────────
export interface FileItem {
  id?: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  file?: File;
  content?: string; // base64 data URI (compat with UploadedFile)
  progress?: number;
  error?: string;
  thumbnailUrl?: string;
}

// ── Helpers ──────────────────────────────────────────
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageFile = (name: string, type: string): boolean => {
  if (type.startsWith("image/")) return true;
  const ext = name.toLowerCase().split(".").pop() || "";
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
};

const getFileIconSvg = (name: string, type: string): React.ReactNode => {
  const ext = name.toLowerCase().split(".").pop() || "";

  // PDF icon
  if (ext === "pdf" || type === "application/pdf") {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <rect x="3" y="2" width="18" height="20" rx="2" fill="#FEE2E2" stroke="#DC2626" strokeWidth="1.2" />
        <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="600" fill="#DC2626" fontFamily="var(--font-mono)">PDF</text>
      </svg>
    );
  }

  // Image icon
  if (isImageFile(name, type)) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <rect x="3" y="3" width="18" height="18" rx="2" fill="#FDF5F2" stroke="#D4572B" strokeWidth="1.2" />
        <circle cx="9" cy="9" r="2" fill="#D4572B" opacity="0.5" />
        <path d="M3 17l5-5 3 3 4-4 6 6v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2z" fill="#D4572B" opacity="0.25" />
      </svg>
    );
  }

  // Generic doc icon
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#F2F0EB" stroke="#8C9099" strokeWidth="1.2" />
      <path d="M14 2v6h6" stroke="#8C9099" strokeWidth="1.2" />
    </svg>
  );
};

// ── Download icon ────────────────────────────────────
const DownloadIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v8m0 0l-3-3m3 3l3-3M3 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Retry icon ───────────────────────────────────────
const RetryIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 8a6 6 0 0110.89-3.48M14 2v4h-4M14 8a6 6 0 01-10.89 3.48M2 14v-4h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Close / cancel icon ──────────────────────────────
const CloseIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ── FileRow component ────────────────────────────────
interface FileRowProps {
  file: FileItem;
  index: number;
  variant: "default" | "compact" | "image-preview";
  isPhone: boolean;
  onRemove: (index: number) => void;
  onDownload?: (file: FileItem) => void;
  onRetry?: (file: FileItem, index: number) => void;
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  index,
  variant,
  isPhone,
  onRemove,
  onDownload,
  onRetry,
}) => {
  const hasError = !!file.error;
  const isUploading = file.progress !== undefined && file.progress < 100 && !hasError;
  const isImage = isImageFile(file.name, file.type);

  // Thumbnail for image-preview variant
  const thumbnailSrc = useMemo(() => {
    if (variant !== "image-preview" || !isImage) return null;
    if (file.thumbnailUrl) return file.thumbnailUrl;
    if (file.url) return file.url;
    if (file.file) return URL.createObjectURL(file.file);
    if (file.content) return file.content;
    return null;
  }, [variant, isImage, file.thumbnailUrl, file.url, file.file, file.content]);

  const showThumbnail = variant === "image-preview" && isImage && thumbnailSrc;

  // Row classes
  const rowBase = "flex items-center gap-3 rounded-card border transition-all duration-200 group";
  const rowPadding = isPhone || variant === "compact" ? "px-3 py-2.5" : "px-4 py-3";
  const rowBg = hasError
    ? "bg-white border-l-[3px] border-l-[#DC2626] border-[#E8E5DF]"
    : "bg-[#FAFAF8] border-[#E8E5DF] hover:bg-white hover:shadow-sm";

  return (
    <div className={`${rowBase} ${rowPadding} ${rowBg}`} style={{ animationDelay: `${index * 50}ms` }}>
      {/* Icon or thumbnail */}
      {showThumbnail ? (
        <img
          src={thumbnailSrc}
          alt={file.name}
          className="w-12 h-12 rounded-badge object-cover shrink-0"
        />
      ) : (
        <span className={hasError ? "text-[#DC2626]" : "text-mid-muted"}>
          {getFileIconSvg(file.name, file.type)}
        </span>
      )}

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            className={`font-sora text-[13px] font-medium truncate ${
              hasError ? "text-[#DC2626]" : "text-mid-charcoal"
            }`}
          >
            {file.name}
          </span>
          {!isPhone && (
            <span className="font-mono text-[11px] text-mid-muted shrink-0">
              {formatFileSize(file.size)}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {isUploading && (
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex-1 h-1 bg-[#F2F0EB] rounded-full overflow-hidden">
              <div
                className="h-full bg-mid-terracotta rounded-full transition-all duration-300"
                style={{ width: `${file.progress}%` }}
              />
            </div>
            <span className="font-mono text-[12px] text-mid-terracotta font-medium shrink-0">
              {file.progress}%
            </span>
          </div>
        )}

        {/* Error message */}
        {hasError && (
          <p className="font-sora text-[12px] text-[#DC2626] mt-0.5" role="alert">
            {file.error}
          </p>
        )}

        {/* Phone: show size on second line */}
        {isPhone && !isUploading && !hasError && (
          <span className="font-mono text-[11px] text-mid-muted">
            {formatFileSize(file.size)}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Retry button for size/type errors */}
        {hasError && file.error?.includes("exceeds") && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(file, index)}
            className="p-1.5 rounded-btn text-mid-muted hover:text-mid-terracotta hover:bg-[#FDF5F2] transition-colors"
            aria-label={`Retry ${file.name}`}
          >
            <RetryIcon />
          </button>
        )}

        {/* Download button (only for uploaded files with URL) */}
        {!isUploading && !hasError && (file.url || file.content) && (
          <button
            type="button"
            onClick={() => onDownload?.(file)}
            className="p-1.5 rounded-btn text-mid-muted hover:text-mid-terracotta hover:bg-[#FDF5F2] transition-colors"
            aria-label={`Download ${file.name}`}
          >
            <DownloadIcon />
          </button>
        )}

        {/* Remove / cancel button */}
        <button
          type="button"
          onClick={() => onRemove(index)}
          className={`p-1.5 rounded-btn transition-colors ${
            hasError
              ? "text-[#DC2626] hover:bg-red-50"
              : "text-mid-muted hover:text-[#DC2626] hover:bg-red-50"
          }`}
          aria-label={isUploading ? `Cancel upload ${file.name}` : `Delete ${file.name}`}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

export default FileRow;
export { formatFileSize, isImageFile, getFileIconSvg, CloseIcon };
